import { clearLocalStorage } from "@/utils/localStorage"
import type { ApiError } from "@/types/api"
import type { AxiosInstance } from "axios"

const CODE_UNAUTHORIZED = 401
const DETAILS_HAS_TO_REFRESH = ["api020", "Signature has expired"]
const DETAILS_DISCONNECTED = [
  "Token has been revoked",
  "Missing Authorization Header",
  "Signature verification failed"
]
const URLS_HAS_NO_REFRESH_TOKEN = [
  "/api/v1/refresh",
  "/api/v1/login",
  "/api/v1/first-access",
  "/api/v1/forgot-password",
  "/api/v1/double-factor/generate_qr_code",
  "/api/v1/access_revoke",
  "/api/v1/refresh_revoke"
]
const URLS_USE_REFRESH_TOKEN = ["/api/v1/refresh", "/api/v1/refresh_revoke"]

const setup = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    config => {
      let token = null
      if (!URLS_USE_REFRESH_TOKEN.includes(config.url || "")) {
        token =
          localStorage.getItem("access_token") || localStorage.getItem("token")
      } else {
        token = localStorage.getItem("refresh_token")
      }
      if (token) {
        if (config.headers) config.headers["Authorization"] = token
      }
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    res => {
      return res
    },
    async err => {
      const originalConfig = err.config
      if (hasToRefreshToken(err)) {
        // Access Token was expired
        if (
          err.response.status === CODE_UNAUTHORIZED &&
          !originalConfig._retry
        ) {
          originalConfig._retry = true

          try {
            const rs = await instance.post("/api/v1/refresh")

            const { access_token } = rs.data
            localStorage.setItem("access_token", access_token)

            return instance(originalConfig)
          } catch (_error) {
            window.location.href = "/logout"
            return Promise.reject(_error)
          }
        }
      } else if (isDisconnected(err)) {
        clearLocalStorage()
        window.location.href = "/login"
      }

      return Promise.reject(err)
    }
  )
}

function hasToRefreshToken(err: ApiError) {
  const detail = err.response ? err.response.data.detail : ""
  return (
    !URLS_HAS_NO_REFRESH_TOKEN.includes(err.config?.url || "") &&
    DETAILS_HAS_TO_REFRESH.includes(detail)
  )
}

function isDisconnected(err: ApiError) {
  const detail = err.response ? err.response.data.detail : ""
  return DETAILS_DISCONNECTED.includes(detail)
}

export default setup
